import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import type { ImageProps } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { FolderFile } from './clientCard.model';
import { fetchFolderFileBlob, folderFileKey as fileKey } from './folderFile';

/** Shown for the frame of an image whose bytes are still on the way. */
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

type FolderPreview = {
  /** Start the slideshow on this file. */
  open: (file: FolderFile) => void;
  /** Spread onto an `Image.PreviewGroup`. */
  groupProps: {
    items: ImageProps[];
    preview: {
      visible: boolean;
      current: number;
      onVisibleChange: (value: boolean) => void;
      onChange: (current: number) => void;
    };
  };
};

/**
 * The folder's images as one slideshow, the way a visit's attachments already work.
 *
 * The grid only has 256px thumbnails, so the full-size bytes are fetched when the
 * viewer opens rather than up front — a folder of scans is megabytes, and most of
 * them are never looked at. The thumbnail stands in for the frame until its image
 * arrives, and the neighbours are fetched alongside so an arrow press is instant.
 */
const useFolderPreview = (images: FolderFile[]): FolderPreview => {
  const { handleError } = useSmartErrorHandling();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [urls, setUrls] = useState<Record<string, string>>({});

  const urlsRef = useRef<Record<string, string>>({});
  const pendingRef = useRef<Set<string>>(new Set());
  // The list changes with the filter chips and is rebuilt every render, so the
  // loaders read it through a ref instead of closing over one render's array.
  const imagesRef = useRef(images);
  imagesRef.current = images;

  // Object URLs outlive the fetch that made them; nothing else frees these.
  useEffect(() => {
    const held = urlsRef.current;
    return () => Object.values(held).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const ensure = useCallback(
    async (file: FolderFile | undefined) => {
      if (!file) return;
      const key = fileKey(file);
      if (urlsRef.current[key] || pendingRef.current.has(key)) return;

      pendingRef.current.add(key);
      try {
        const url = URL.createObjectURL(await fetchFolderFileBlob(file));
        urlsRef.current[key] = url;
        setUrls((prev) => ({ ...prev, [key]: url }));
      } catch (error) {
        handleError(error as Error);
      } finally {
        pendingRef.current.delete(key);
      }
    },
    [handleError]
  );

  const load = useCallback(
    (index: number) => {
      [index, index + 1, index - 1].forEach((one) => void ensure(imagesRef.current[one]));
    },
    [ensure]
  );

  const open = useCallback(
    (file: FolderFile) => {
      const index = imagesRef.current.findIndex((one) => fileKey(one) === fileKey(file));
      if (index < 0) return;
      setCurrent(index);
      setVisible(true);
      load(index);
    },
    [load]
  );

  const items: ImageProps[] = images.map((file) => ({
    src:
      urls[fileKey(file)] ??
      (file.attachment.thumbnail ? `data:image/jpeg;base64,${file.attachment.thumbnail}` : BLANK),
    alt: file.attachment.filename,
  }));

  return {
    open,
    groupProps: {
      items,
      preview: {
        visible,
        current,
        onVisibleChange: setVisible,
        onChange: (next: number) => {
          setCurrent(next);
          load(next);
        },
      },
    },
  };
};

export default useFolderPreview;
